import { buildFrag } from "@/lib/glsl";

const VERT_SRC =
    "attribute vec2 a_pos; varying vec2 v_uv; void main(){ v_uv = vec2(a_pos.x * 0.5 + 0.5, 0.5 - a_pos.y * 0.5); gl_Position = vec4(a_pos, 0.0, 1.0); }";

// One WebGL engine per canvas. Programs are cached per effect body, the video
// texture is re-uploaded each frame from the graded 2D canvas.
export function createEngine(canvas) {
    const gl = canvas.getContext("webgl", {
        preserveDrawingBuffer: true,
        antialias: false,
        premultipliedAlpha: false,
    });
    if (!gl) return null;

    const cache = new Map();
    const texture = gl.createTexture();
    const buffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW);

    function compile(type, src) {
        const s = gl.createShader(type);
        gl.shaderSource(s, src);
        gl.compileShader(s);
        if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) {
            const info = gl.getShaderInfoLog(s);
            gl.deleteShader(s);
            throw new Error(info || "shader compile failed");
        }
        return s;
    }

    function getProgram(body) {
        let entry = cache.get(body);
        if (entry) return entry;
        const vs = compile(gl.VERTEX_SHADER, VERT_SRC);
        const fs = compile(gl.FRAGMENT_SHADER, buildFrag(body));
        const p = gl.createProgram();
        gl.attachShader(p, vs);
        gl.attachShader(p, fs);
        gl.linkProgram(p);
        if (!gl.getProgramParameter(p, gl.LINK_STATUS)) {
            throw new Error(gl.getProgramInfoLog(p) || "program link failed");
        }
        entry = { prog: p, aPos: gl.getAttribLocation(p, "a_pos"), u: {} };
        for (const n of ["u_image", "u_res", "u_time", "u_prog", "u_intensity", "u_speed", "u_amount", "u_opacity", "u_exposure", "u_highlights", "u_shadows", "u_temp", "u_tint", "u_fade", "u_sharp"]) {
            entry.u[n] = gl.getUniformLocation(p, n);
        }
        cache.set(body, entry);
        return entry;
    }

    function render(body, source, o) {
        let entry;
        try {
            entry = getProgram(body);
        } catch (e) {
            console.warn("effect shader failed:", e && e.message);
            return false;
        }
        gl.useProgram(entry.prog);
        gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
        gl.enableVertexAttribArray(entry.aPos);
        gl.vertexAttribPointer(entry.aPos, 2, gl.FLOAT, false, 0, 0);
        gl.activeTexture(gl.TEXTURE0);
        gl.bindTexture(gl.TEXTURE_2D, texture);
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, source);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
        gl.viewport(0, 0, canvas.width, canvas.height);
        gl.uniform1i(entry.u.u_image, 0);
        gl.uniform2f(entry.u.u_res, canvas.width, canvas.height);
        gl.uniform1f(entry.u.u_time, o.time || 0);
        gl.uniform1f(entry.u.u_prog, typeof o.prog === "number" ? o.prog : 1);
        gl.uniform1f(entry.u.u_intensity, typeof o.intensity === "number" ? o.intensity : 1);
        gl.uniform1f(entry.u.u_speed, o.speed || 1);
        gl.uniform1f(entry.u.u_amount, typeof o.amount === "number" ? o.amount : 0.5);
        gl.uniform1f(entry.u.u_opacity, typeof o.opacity === "number" ? o.opacity : 1);
        gl.uniform1f(entry.u.u_exposure, typeof o.exposure === "number" ? o.exposure : 1);
        gl.uniform1f(entry.u.u_highlights, typeof o.highlights === "number" ? o.highlights : 0);
        gl.uniform1f(entry.u.u_shadows, typeof o.shadows === "number" ? o.shadows : 0);
        gl.uniform1f(entry.u.u_temp, typeof o.temp === "number" ? o.temp : 0);
        gl.uniform1f(entry.u.u_tint, typeof o.tint === "number" ? o.tint : 0);
        gl.uniform1f(entry.u.u_fade, typeof o.fade === "number" ? o.fade : 0);
        gl.uniform1f(entry.u.u_sharp, typeof o.sharp === "number" ? o.sharp : 0);
        gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
        return true;
    }

    return { gl, render };
}
